import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Domain } from '@app/shared/models/domain';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Provider } from '@app/shared/models/provider';
import { DomainSettings } from '@app/shared/models/domainSettings';
import { DomainCustomization } from '@app/shared/models/domainCustomization';
import { isPlatformServer } from '@angular/common';
import { Request } from 'express';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { CreateDomainInput, GetDomainOutput } from '@smash-sdk/domain/01-2024';
const DOMAIN_URI = 'domain.fromsmash.co';
const domainApiVersion112023 = '11-2023';

@Injectable({
  providedIn: 'root'
})
export class DomainService {

  headers = new HttpHeaders().set('Authorization', 'true');

  constructor(
    private http: HttpClient,
    @Optional() @Inject(REQUEST) private req: Request,
    @Inject(DOCUMENT) private document,
    @Inject(PLATFORM_ID) private platformId: any) { }

  getDomain(name: string): Observable<GetDomainOutput> {
    return this.http.get<GetDomainOutput>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain/' + name + '?version=01-2024', { headers: this.headers });
  }

  updateDomain(
    name: string,
    data: {
      domain?: string,
      options?: DomainSettings,
    }
  ): Observable<{ domain: Domain }> {
    return this.http.put<{ domain: Domain }>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain/' + name + '?version=01-2024', data, { headers: this.headers });
  }

  createDomain(
    data: CreateDomainInput
  ): Observable<{ domain: Domain }> {
    return this.http.post<{ domain: Domain }>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain' + '?version=01-2024', data, { headers: this.headers });
  }

  deleteDomain(name: string): Observable<{}> {
    return this.http.delete<{}>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain/' + name, { headers: this.headers });
  }

  updateDomainCustomization(
    domain: string,
    data: {
      logo: string,
      background: string,
    }
  ): Observable<{ customization: DomainCustomization }> {
    return this.http.put<{ customization: DomainCustomization }>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain/' + domain + '/customization', data, { headers: this.headers });
  }

  getDomainCustomization(domain: string): Observable<{ customization: DomainCustomization }> {
    return this.http.get<{ customization: DomainCustomization }>('https://' + environment.prefixUrl + DOMAIN_URI + '/domain/' + domain + '/customization', { headers: this.headers });
  }

  getRecord(name: string): Observable<{ record: Domain }> {
    return this.http.get<{ record: Domain }>('https://' + environment.prefixUrl + DOMAIN_URI + '/record/' + name + '?version=01-2024');
  }

  listRecordProviders(id: string): Observable<{ providers: Provider[] }> {
    return this.http.get<{ providers: Provider[] }>('https://' + environment.prefixUrl + DOMAIN_URI + '/record/' + id + '/providers' + '?version=' + domainApiVersion112023);
  }

  detectDomain() {
    if (isPlatformServer(this.platformId)) {
      const ext = environment.production ? '.com' : '.ninja';
      return 'fromsmash' + ext; // TO FIX wait for X-forwarded-host header to work
    }
    return document.location.hostname;
  }

  detectDomainUrl() {
    if (isPlatformServer(this.platformId)) {
      return this.req.protocol + '://' + this.detectDomain() + this.req.baseUrl;
    }
    return document.location.href;
  }

  detectDomainUrlWithoutProtocol() {
    if (isPlatformServer(this.platformId)) {
      return this.detectDomain() + this.req.originalUrl;
    }
    return document.location.href.replace(/https?:\/\//i, '');
  }

  detectDomainUrlWithoutQueryString() {
    if (isPlatformServer(this.platformId)) {
      return this.detectDomain() + this.req.originalUrl;
    }
    return document.location.hostname + document.location.pathname;
  }

  detectPath() {
    if (isPlatformServer(this.platformId)) {
      return this.req.originalUrl;
    }
    return document.location.pathname;
  }

  loadDomains() {
    return this.http.get<{ domains: Domain[] }>('https://' + environment.prefixUrl + DOMAIN_URI + '/domains', { headers: this.headers });
  }
}
